import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchBySlug } from '../lib/api';
import { MapPin, Phone, Mail, FileText, ArrowRight, ShieldCheck, Facebook, Linkedin } from 'lucide-react';

export default function Contact() {
  const [contactData, setContactData] = useState(null);
  const [jobData, setJobData] = useState(null);
  const contentRef = useRef(null);
  const jobSectionRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    async function loadData() {
      const cData = await fetchBySlug('da', 'kontakt');
      setContactData(cData);

      const jData = await fetchBySlug('da', 'uopfordret-ansoegning');
      setJobData(jData);
    }
    loadData();
  }, []);

  useEffect(() => {
    if (location.pathname === '/kontakt/uopfordret-ansoegning' && jobSectionRef.current && jobData) {
      const timer = setTimeout(() => {
        jobSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, jobData]);

  useEffect(() => {
    if (contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach(link => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    }
  }, [jobData]);

  if (!contactData || !jobData) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">
        <div className="animate-pulse text-sm font-semibold tracking-widest uppercase">Henter indhold...</div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{contactData.meta_title || 'Kontakt – Møldrup VVS A/S'}</title>
        <meta name="description" content={contactData.meta_description || ''} />
        <html lang="da" />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative bg-slate-950 text-white py-24 md:py-32 border-b border-slate-900">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/2750d3ac-cba1-4299-92fa-1fa2c5bb200b/0cd8fe7f-0ba4-47e3-908a-4fc623bd309c.jpg"
            alt="Kontakt"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-12">
          <span className="text-blue-500 text-xs font-extrabold tracking-[0.25em] uppercase block mb-3">
            Tag fat i os
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-wider uppercase">
            Kontakt Os
          </h1>
        </div>
      </section>

      {/* Contact Details & Map Section */}
      <section className="py-24 bg-white text-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Contact Info */}
            <div className="lg:col-span-5 space-y-8">
              <span className="text-blue-600 text-xs font-extrabold tracking-[0.25em] uppercase block">
                Information
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-wider uppercase">
                Møldrup VVS A/S
              </h2>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-slate-100 p-3 text-blue-600">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-extrabold uppercase tracking-wider text-xs text-gray-400 mb-1">Adresse</h3>
                    <p className="font-semibold text-slate-800">
                      Møllegade 4<br />
                      9632 Møldrup<br />
                      Danmark
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-slate-100 p-3 text-blue-600">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-extrabold uppercase tracking-wider text-xs text-gray-400 mb-1">Telefon</h3>
                    <a href="tel:+4586692099" className="font-semibold text-slate-800 hover:text-blue-600 transition-colors duration-150 whitespace-nowrap text-base">
                      86 69 20 99
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-slate-100 p-3 text-blue-600">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-extrabold uppercase tracking-wider text-xs text-gray-400 mb-1">E-mail</h3>
                    <a href="mailto:info@moeldrupvvs.dk" className="font-semibold text-slate-800 hover:text-blue-600 transition-colors duration-150 whitespace-nowrap text-base">
                      info@moeldrupvvs.dk
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-slate-100 p-3 text-blue-600">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-extrabold uppercase tracking-wider text-xs text-gray-400 mb-1">CVR Nummer</h3>
                    <p className="font-semibold text-slate-800 whitespace-nowrap">
                      20966998
                    </p>
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-100">
                  <h3 className="font-extrabold uppercase tracking-wider text-xs text-gray-400 mb-3">Følg os på sociale medier</h3>
                  <div className="flex items-center space-x-3">
                    <a
                      href="https://www.facebook.com/profile.php?id=61575205066173"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-slate-100 hover:bg-blue-600 text-slate-700 hover:text-white p-3 transition-colors duration-150"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                    <a
                      href="https://www.linkedin.com/company/m%C3%B8ldrup-vvs-a-s"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-slate-100 hover:bg-blue-600 text-slate-700 hover:text-white p-3 transition-colors duration-150"
                      aria-label="LinkedIn"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Google Maps Link / Visual Block */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-blue-600 text-xs font-extrabold tracking-[0.25em] uppercase block">
                Find os på kortet
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-wider uppercase">
                Vores Beliggenhed
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                Vi har base i Møldrup, men udfører VVS-arbejde i det meste af Danmark, herunder Hobro, Viborg, Randers og omegn. Klik på knappen nedenfor for at finde rutevejledning på Google Maps.
              </p>
              <div className="pt-4">
                <a
                  href="https://www.google.dk/maps/place/M%C3%B8ldrup+VVS+A%2FS/@56.613802,9.5000016,17z/data=!3m1!4b1!4m5!3m4!1s0x464959b0904cf551:0x845a8ee9be7955a!8m2!3d56.613802!4d9.5021956"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-700 hover:bg-blue-800 text-white font-bold tracking-widest uppercase px-8 py-4 rounded-none transition-colors duration-200 text-xs inline-flex items-center space-x-2"
                >
                  <span>Åben Google Maps</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job / Uopfordret Ansøgning Section */}
      <section ref={jobSectionRef} className="py-24 bg-slate-50 text-slate-900 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6 space-y-8">
          <div className="text-center">
            <div className="bg-blue-100 p-4 text-blue-600 inline-block rounded-none mb-4">
              <FileText className="w-8 h-8" />
            </div>
            <span className="text-blue-600 text-xs font-extrabold tracking-[0.25em] uppercase block">
              Karriere
            </span>
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-wider uppercase mb-8">
              {jobData.title}
            </h2>
          </div>
          <div className="max-w-3xl mx-auto [&_p]:mb-6 [&_p]:text-slate-600 [&_p]:leading-relaxed [&_p]:text-base [&_h1]:text-3xl [&_h1]:font-extrabold [&_h1]:mt-8 [&_h1]:mb-4 [&_h1]:uppercase [&_h1]:tracking-wider [&_h1]:text-slate-900 [&_h2]:text-2xl [&_h2]:font-extrabold [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:uppercase [&_h2]:tracking-wider [&_h2]:text-slate-900 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:uppercase [&_h3]:tracking-wider [&_h3]:text-slate-900 [&_ul]:my-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-6 [&_ol]:space-y-2 [&_li]:text-slate-600 [&_li]:leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_a]:font-semibold hover:[&_a]:text-blue-800 [&_a]:transition-colors [&_strong]:font-bold [&_strong]:text-slate-900">
            <div ref={contentRef} dangerouslySetInnerHTML={{ __html: jobData.content }} />
          </div>
        </div>
      </section>
    </>
  );
}
